import { useEffect, useMemo, useState } from "react";
import Icon from "./Icon";
import {
  createFourWeekRotationDraft,
  refreshFourWeekRotationDraft,
  getFourWeekRotationPublicationIssue,
  hasFourDistinctResolvedWeeks,
  parseFourWeekRotationOverride,
  validateFourWeekRotation,
} from "../services/fourWeekRotation";

const cardStyle = {
  border: "1px solid rgba(212, 201, 168, 0.16)",
  borderRadius: 12,
  background: "rgba(255,255,255,0.025)",
  overflow: "hidden",
};

const buttonStyle = {
  borderRadius: 7,
  border: "1px solid rgba(212, 201, 168, 0.18)",
  background: "transparent",
  color: "#cfcfc4",
  fontFamily: "inherit",
  fontSize: ".74rem",
  padding: "7px 10px",
  cursor: "pointer",
};

function selectedMealChoice(meal, choiceId) {
  if (choiceId === "primary") return meal;
  return meal.alternatives?.find((alternative) => alternative.id === choiceId) || null;
}

function detailedChoices(meal) {
  const options = [];
  if (meal?.recipe) options.push({ id: "primary", title: meal.title });
  for (const alternative of meal?.alternatives || []) {
    if (alternative?.recipe && alternative.id !== "primary") {
      options.push({ id: alternative.id, title: alternative.title });
    }
  }
  return options;
}

function buildWeekPreview(weekMeals, week) {
  return (weekMeals?.days || []).map((day) => ({
    id: day.id,
    label: day.label,
    meals: (day.meals || []).map((meal) => {
      const selected = selectedMealChoice(meal, week.choices[meal.id]);
      return {
        id: meal.id,
        slotLabel: meal.slot_label || meal.slot,
        title: selected?.title || "À choisir",
        hint: selected?.hint || "",
        ingredients: Array.isArray(selected?.recipe?.ingredients)
          ? selected.recipe.ingredients.slice(0, 4)
          : [],
      };
    }),
  }));
}

export default function FourWeekRotationEditorialCard({
  weekMeals,
  value,
  onSave,
  onDirtyChange,
  onOpenRecipes,
  weekMenus = null,
}) {
  const parsed = useMemo(() => parseFourWeekRotationOverride(value), [value]);
  const [draft, setDraft] = useState(parsed);
  const [baseline, setBaseline] = useState(parsed);
  const [activeWeek, setActiveWeek] = useState(0);
  const [repeatConfirmed, setRepeatConfirmed] = useState(parsed?.status === "validated");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(parsed);
    setBaseline(parsed);
    setActiveWeek(0);
    setRepeatConfirmed(parsed?.status === "validated");
    setError(null);
  }, [parsed]);

  const dirty = Boolean(draft) && JSON.stringify(draft) !== JSON.stringify(baseline);
  useEffect(() => {
    onDirtyChange?.("four_week_rotation", dirty);
    return () => onDirtyChange?.("four_week_rotation", false);
  }, [dirty, onDirtyChange]);

  // Messages précis du même réservoir que l'éditeur Semaine, à la place du
  // message générique de la rotation.
  const preciseIssues = [
    ...(weekMenus?.issues || []),
    ...(weekMenus?.rotation?.issues || []),
  ].filter((issue, index, all) => all.indexOf(issue) === index).slice(0, 8);
  const publicationIssue = getFourWeekRotationPublicationIssue(weekMeals, draft);
  const cycleHasFourDistinctWeeks = hasFourDistinctResolvedWeeks(weekMeals, draft);
  const weekPreview = draft?.weeks?.[activeWeek]
    ? buildWeekPreview(weekMeals, draft.weeks[activeWeek])
    : [];

  function prepare() {
    try {
      setDraft(draft
        ? refreshFourWeekRotationDraft(weekMeals, draft, { fillMissing: false })
        : createFourWeekRotationDraft(weekMeals));
      setRepeatConfirmed(false);
      setActiveWeek(0);
      setSaved(false);
      setError(null);
    } catch (nextError) {
      setError(nextError?.message || "Les 4 semaines ne peuvent pas être préparées.");
    }
  }

  async function persist(next) {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      if (typeof onSave !== "function") throw new Error("Enregistrement indisponible dans cette étape.");
      await onSave(next);
      setDraft(next);
      setBaseline(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
      return true;
    } catch (nextError) {
      setError(nextError?.message || "Enregistrement impossible.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    if (!draft) return;
    await persist({ ...draft, status: "draft" });
  }

  async function validate() {
    if (!draft || dirty || !repeatConfirmed) return;
    try {
      await persist(validateFourWeekRotation(weekMeals, draft));
    } catch (nextError) {
      setError(nextError?.message || "Validation impossible.");
    }
  }

  function changeChoice(mealId, choiceId) {
    setDraft((current) => ({
      ...current,
      status: "draft",
      weeks: current.weeks.map((week, index) => index === activeWeek
        ? { ...week, choices: { ...week.choices, [mealId]: choiceId } }
        : week),
    }));
    setRepeatConfirmed(false);
    setSaved(false);
  }

  return (
    <section style={cardStyle} aria-labelledby="four-week-rotation-title">
      <header style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: 18 }}>
        <Icon type="calendar" size={18} style={{ color: "#d4c9a8", marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, color: "#8a8a7a", fontSize: ".66rem", fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase" }}>
            Organisation du programme
          </p>
          <h4 id="four-week-rotation-title" style={{ margin: "4px 0 0", color: "#f0f0e8", fontSize: "1rem" }}>
            Cycle de 4 semaines
          </h4>
          <p style={{ margin: "5px 0 0", color: "#99998d", fontSize: ".78rem", lineHeight: 1.55 }}>
            Répartit uniquement les menus et recettes déjà approuvés. Vérifie le cycle, puis valide-le pour l’application.
          </p>
        </div>
        <button
          type="button"
          onClick={prepare}
          disabled={saving || typeof onSave !== "function"}
          style={{ ...buttonStyle, color: "#d4c9a8", cursor: saving ? "wait" : "pointer" }}
        >
          {draft ? "Actualiser" : "Préparer les 4 semaines"}
        </button>
      </header>

      {value && !parsed && (
        <p role="alert" style={{ margin: "0 18px 18px", color: "#f5c6c6", fontSize: ".76rem" }}>
          Rotation enregistrée non conforme. Actualise-la avant toute publication.
        </p>
      )}

      {error && !draft && (
        <div
          role="alert"
          style={{
            margin: "0 18px 18px",
            padding: 12,
            borderRadius: 9,
            border: "1px solid rgba(229, 184, 120, 0.32)",
            background: "rgba(229, 184, 120, 0.08)",
            color: "#f2d39f",
            fontSize: ".76rem",
            lineHeight: 1.5,
          }}
        >
          <strong style={{ display: "block", marginBottom: 4 }}>Les recettes ne sont pas encore assez variées.</strong>
          {preciseIssues.length > 0 ? (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {preciseIssues.map((issue) => <li key={issue}>{issue}</li>)}
            </ul>
          ) : <span>{error}</span>}
          <span style={{ display: "block", marginTop: 5 }}>
            Ajoute ou complète les alternatives dans « Semaine, menus & recettes » (onglets Semaine 1 à 4), puis reviens actualiser le cycle.
          </span>
          {typeof onOpenRecipes === "function" && (
            <button
              type="button"
              onClick={onOpenRecipes}
              style={{ ...buttonStyle, minHeight: 44, marginTop: 10, color: "#f2d39f", borderColor: "rgba(229, 184, 120, 0.38)" }}
            >
              Modifier les alternatives
            </button>
          )}
        </div>
      )}

      {weekMenus?.rotation?.message && weekMenus.rotation.status === "to_refresh" && (
        <p role="status" style={{ margin: "0 18px 18px", color: "#e5c878", fontSize: ".76rem", lineHeight: 1.5 }}>
          {weekMenus.rotation.message}
        </p>
      )}

      {draft && (
        <div style={{ borderTop: "1px solid rgba(212,201,168,.10)", padding: 18 }}>
          <div role="tablist" aria-label="Semaines du cycle" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 7 }}>
            {draft.weeks.map((week, index) => (
              <button
                key={week.id}
                type="button"
                role="tab"
                aria-selected={activeWeek === index}
                onClick={() => setActiveWeek(index)}
                style={{
                  ...buttonStyle,
                  borderColor: activeWeek === index ? "rgba(138,191,154,.45)" : buttonStyle.border,
                  background: activeWeek === index ? "rgba(138,191,154,.11)" : "transparent",
                  color: activeWeek === index ? "#a8e0c0" : "#99998d",
                }}
              >{week.label}</button>
            ))}
          </div>

          <div role="tabpanel" style={{ display: "grid", gap: 8, marginTop: 14 }}>
            {weekPreview.map((day) => (
              <div key={day.id} style={{ borderRadius: 9, background: "rgba(0,0,0,.12)", padding: "10px 12px" }}>
                <strong style={{ display: "block", color: "#d4c9a8", fontSize: ".74rem", marginBottom: 6 }}>{day.label}</strong>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {day.meals.map((mealPreview) => {
                    const sourceMeal = (weekMeals?.days || [])
                      .flatMap((sourceDay) => sourceDay.meals || [])
                      .find((meal) => meal.id === mealPreview.id);
                    const choiceId = draft.weeks[activeWeek].choices[mealPreview.id];
                    return (
                      <label key={mealPreview.id} style={{ display: "grid", gap: 3, minWidth: 220, flex: "1 1 220px", color: "#cfcfc4", fontSize: ".72rem", lineHeight: 1.4 }}>
                        <span style={{ color: "#77776e" }}>{mealPreview.slotLabel}</span>
                        <select
                          aria-label={`${draft.weeks[activeWeek].label} · ${day.label} · ${mealPreview.slotLabel}`}
                          value={detailedChoices(sourceMeal).some((choice) => choice.id === choiceId) ? choiceId : ""}
                          onChange={(event) => { if (event.target.value) changeChoice(mealPreview.id, event.target.value); }}
                          disabled={saving || typeof onSave !== "function"}
                          style={{ ...buttonStyle, background: "#20201f", color: "#cfcfc4", width: "100%" }}
                        >
                          {!detailedChoices(sourceMeal).some((choice) => choice.id === choiceId) && (
                            <option value="">À choisir</option>
                          )}
                          {detailedChoices(sourceMeal).map((choice) => (
                            <option key={choice.id} value={choice.id}>{choice.title}</option>
                          ))}
                        </select>
                        {(mealPreview.hint || mealPreview.ingredients.length > 0) && (
                          <span style={{
                            display: "block",
                            minHeight: 30,
                            color: "#aaa99f",
                            fontSize: ".68rem",
                            lineHeight: 1.45,
                            padding: "4px 2px 0",
                          }}>
                            {mealPreview.hint && <span style={{ display: "block", color: "#c9c1aa" }}>{mealPreview.hint}</span>}
                            {mealPreview.ingredients.length > 0 && (
                              <span style={{ display: "block" }}>{mealPreview.ingredients.join(" · ")}</span>
                            )}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 9, marginTop: 14, color: "#cfcfc4", fontSize: ".76rem", lineHeight: 1.45 }}>
            <input
              type="checkbox"
              checked={repeatConfirmed}
              onChange={(event) => setRepeatConfirmed(event.target.checked)}
              disabled={saving || draft.status === "validated" || !cycleHasFourDistinctWeeks}
              style={{ marginTop: 2 }}
            />
            <span>Je confirme que ce cycle recommence après la semaine 4.</span>
          </label>

          {(error || publicationIssue || saved) && (
            <p role={error ? "alert" : "status"} style={{ margin: "12px 0 0", color: error ? "#f5c6c6" : publicationIssue ? "#e5c878" : "#8abf9a", fontSize: ".76rem" }}>
              {error || (saved ? "Rotation enregistrée" : publicationIssue)}
            </p>
          )}
          {!saved && (error || publicationIssue) && preciseIssues.length > 0 && (
            <div style={{ marginTop: 6, color: "#e5c878", fontSize: ".74rem", lineHeight: 1.5 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {preciseIssues.map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
              {typeof onOpenRecipes === "function" && (
                <button
                  type="button"
                  onClick={onOpenRecipes}
                  style={{ ...buttonStyle, minHeight: 44, marginTop: 8, color: "#f2d39f", borderColor: "rgba(229, 184, 120, 0.38)" }}
                >Modifier les alternatives</button>
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => { setDraft(baseline); setRepeatConfirmed(baseline?.status === "validated"); }}
              disabled={!dirty || saving}
              style={{ ...buttonStyle, opacity: !dirty ? .45 : 1 }}
            >Annuler</button>
            <button
              type="button"
              onClick={saveDraft}
              disabled={!dirty || saving}
              style={{ ...buttonStyle, color: "#d4c9a8", opacity: !dirty ? .45 : 1 }}
            >{saving ? "Enregistrement…" : "Enregistrer le brouillon"}</button>
            <button
              type="button"
              onClick={validate}
              disabled={dirty || saving || !repeatConfirmed || !cycleHasFourDistinctWeeks || draft.status === "validated"}
              style={{
                ...buttonStyle,
                borderColor: "rgba(138,191,154,.38)",
                background: "rgba(138,191,154,.12)",
                color: "#a8e0c0",
                opacity: (dirty || !repeatConfirmed || !cycleHasFourDistinctWeeks || draft.status === "validated") ? .45 : 1,
              }}
            >
              {draft.status === "validated" ? "Validé par Anissa" : "Valider les 4 semaines"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
